import React, { useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { House, ArrowLeft } from "@phosphor-icons/react";

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
      toast.success("Check your inbox.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-ink-900 text-zinc-100">
      <div className="w-full max-w-md animate-fade-up">
        <Link to="/login" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-zinc-400 hover:text-gold mb-8" data-testid="back-to-login">
          <ArrowLeft size={12} /> Back to sign in
        </Link>
        <div className="flex items-center gap-2 mb-8">
          <House size={24} weight="duotone" className="text-gold" />
          <span className="font-display text-2xl">Reagent Network</span>
        </div>

        <div className="text-[10px] uppercase tracking-[0.3em] text-gold mb-3">Reset</div>
        <h2 className="font-display text-4xl tracking-tight">Forgot your password?</h2>
        <p className="text-zinc-500 text-sm mt-2">
          Enter the email tied to your agent account. We'll send you a link to set a new one.
        </p>

        {!sent ? (
          <form className="mt-8 space-y-4" onSubmit={onSubmit} data-testid="forgot-form">
            <div>
              <Label className="text-xs uppercase tracking-[0.2em] text-zinc-400">Email</Label>
              <Input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="forgot-email-input"
                className="mt-2 bg-ink-700 border-white/10 focus:border-gold focus-visible:ring-gold h-11"
              />
            </div>
            <Button
              type="submit"
              disabled={loading}
              data-testid="forgot-submit-button"
              className="w-full h-11 bg-gold text-ink-900 hover:bg-gold-light font-semibold"
            >
              {loading ? "Sending..." : "Send reset link →"}
            </Button>
          </form>
        ) : (
          <div className="mt-8 p-5 rounded-lg border hairline bg-white/[0.02]" data-testid="forgot-success">
            <div className="text-sm text-zinc-200">If an account exists for <span className="text-gold">{email}</span>, a reset link has been sent.</div>
            <div className="text-xs text-zinc-500 mt-2">
              Didn't get it? Check your spam folder or{" "}
              <button onClick={() => setSent(false)} className="text-gold hover:text-gold-light underline" data-testid="forgot-retry">try again</button>.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
